import { ForcePasswordChange } from '@auth/features/force-password-change';
import { useAuthData } from '@auth/shared/model';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { reportClientEvent } from '@/shared/lib/report-client-event';
import { Text } from '@/shared/ui';
import { hasPermission, isAdmin } from '../model/permissions';
import css from './auth-guard.module.scss';
import { isTravellerPreviewLink } from './preview-access';
import { goSignIn, sanitizeRedirectUrl } from './redirect-url';
import { getRoutePermission } from './route-permissions';

// Auth-form pages: routes that should NOT be shown to signed-in users (or to
// dev-bypassed sessions). Visiting them while authenticated bounces the user
// to /moodboard.
const AUTH_FORM_ROUTES = ['/sign-in', '/sign-up', '/forgot-password'];

// Public content routes: routes that are reachable without auth but are
// LEGITIMATE destinations regardless of session state. Signed-in or
// dev-bypassed users must remain on these pages — no /moodboard bounce.
// (Preview surfaces are the customer-facing read-only routes; the legacy
// behaviour was to leave signed-in consultants on the preview page so they
// could see exactly what the customer sees via ?view=traveller.)
//
// `/preview` is deliberately NOT listed: it is public only as the traveller
// link — see `isTravellerPreviewLink`, which the `isPublic` check below folds
// in. Its agent view needs a session, as it did in legacy.
const PUBLIC_CONTENT_ROUTES = [
  '/pv/', // legacy short WhatsApp share alias -> resolves + redirects to /preview
  '/operation-preview',
  '/checkout', // public customer payment page (payment-log id is bearer cred)
  '/embed-widget',
  '/customer-preview', // standing discussion link (token in query — Main DB)
  '/final-client-form', // Final Client Details Form share link (token in query)
  '/__ui__', // dev-only component playground (gated out of prod builds)
];

const PUBLIC_ROUTES = [...AUTH_FORM_ROUTES, ...PUBLIC_CONTENT_ROUTES];
const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    redirect_url?: unknown;
    view?: unknown;
  };
  // Path AND search must come from the SAME location object. `useSearch` is a
  // separate subscription that still reports the OUTGOING route's search while
  // `location.pathname` has already advanced to the incoming one. Mid-redirect
  // (`/pv/<short>` → `/preview/<id>?view=traveller`) that mismatch reads as
  // "/preview with no view" — not public — so a signed-out visitor was bounced
  // to sign-in, which cancelled the in-flight navigation and re-armed the
  // alias's redirect: an endless navigate/cancel spin that pegged the tab and
  // never mounted the preview (XT-628).
  const isPublic =
    PUBLIC_ROUTES.some((r) => location.pathname.startsWith(r)) ||
    isTravellerPreviewLink(
      location.pathname,
      location.search as { view?: unknown }
    );
  const isAuthForm = AUTH_FORM_ROUTES.some((r) =>
    location.pathname.startsWith(r)
  );

  // Redirect signed-in (or dev-bypassed) users AWAY FROM auth-form pages.
  // Public content routes are intentionally exempt — preview pages must
  // remain reachable in every session state.
  useEffect(() => {
    if (DEV_BYPASS_AUTH && isAuthForm) {
      navigate({ to: '/moodboard' });
      return;
    }
    if (isLoaded && isSignedIn && isAuthForm) {
      const redirectUrl = sanitizeRedirectUrl(search.redirect_url);
      navigate({ to: redirectUrl ?? '/moodboard' });
    }
  }, [isLoaded, isSignedIn, isAuthForm, navigate, search.redirect_url]);

  if (isPublic) return children;

  if (DEV_BYPASS_AUTH && import.meta.env.PROD) {
    // Don't allow bypass in production
    return <>{children}</>;
  }

  if (DEV_BYPASS_AUTH) {
    return <PermissionGuard>{children}</PermissionGuard>;
  }

  // Protected routes: wait for Clerk to load.
  // (Router beforeLoad already verified a token exists, so this is brief.)
  if (!isLoaded) {
    return (
      <div className={css.loading}>
        <Text variant="muted">Loading...</Text>
      </div>
    );
  }

  // Clerk finished loading and the user is not signed in — the router
  // let us through because a token existed, but Clerk doesn't recognize
  // a session on this domain. Send the user to sign-in instead of
  // hanging on "Loading..." forever.
  if (!isSignedIn) {
    return <SignInRedirect />;
  }

  return <PermissionGuard>{children}</PermissionGuard>;
};

const serializeSearch = (
  search: Record<string, unknown> | string | undefined
): string => {
  if (!search) return '';
  if (typeof search === 'string') {
    return search.startsWith('?') ? search : `?${search}`;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue;
    params.set(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
  const str = params.toString();
  return str ? `?${str}` : '';
};

const SignInRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const search = useSearch({ strict: false }) as Record<string, unknown>;

  useEffect(() => {
    const redirectUrl = location.pathname + serializeSearch(search);
    goSignIn(navigate, redirectUrl);
  }, [navigate, location.pathname, search]);

  return (
    <div className={css.loading}>
      <Text variant="muted">Redirecting…</Text>
    </div>
  );
};

const PermissionGuard = ({ children }: { children: React.ReactNode }) => {
  const { data: authData, isLoading, isError } = useAuthData();
  const user = authData?.user;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (DEV_BYPASS_AUTH) return;
    if (isLoading || !user) return;

    const rule = getRoutePermission(location.pathname);
    if (!rule) return;
    if (isAdmin(user.roles)) return;

    if (rule.roles && !rule.roles.some((r) => user.roles.includes(r))) {
      goSignIn(navigate);
      return;
    }

    if (
      rule.permission &&
      !hasPermission(
        user.permissions ?? [],
        rule.permission.resource,
        rule.permission.action
      )
    ) {
      goSignIn(navigate);
    }
  }, [user, isLoading, location.pathname, navigate]);

  if (isLoading) {
    return (
      <div className={css.loading}>
        <Text variant="muted">Loading...</Text>
      </div>
    );
  }

  // Clerk has a valid session but our backend rejected it — no account
  // matches this Clerk user, or the account isn't Active (e.g. a stale
  // userId left over from a recycled account row, XT-404). Fail closed
  // instead of silently rendering the app shell with no user, which
  // otherwise cascades into other components permanently stuck on
  // "Loading..." (any query gated on `!!user` never fires).
  // Under DEV_BYPASS, auth.me must still resolve the DEV_USER_EMAIL user;
  // if it fails, show the error rather than Clerk sign-out loops.
  if (isError || !user) {
    if (DEV_BYPASS_AUTH) {
      return (
        <div className={css.loading}>
          <Text variant="muted">
            DEV_BYPASS: auth.me failed — check API + DEV_USER_EMAIL
          </Text>
        </div>
      );
    }
    return <OrphanedSessionRedirect />;
  }

  // The user signed in with an admin-issued temporary password (XT-716) and
  // has not replaced it yet. The server already refuses everything but
  // `auth.me` and the clear mutation, so the app shell would render as a wall
  // of failed queries — show the one screen they can act on instead.
  //
  // Rendered in place of `children` rather than redirected to, so there is no
  // route to navigate around it.
  if (user.mustChangePasswordSince) {
    return <ForcePasswordChange />;
  }

  return children;
};

const OrphanedSessionRedirect = () => {
  const { signOut } = useClerk();

  useEffect(() => {
    // XT-404 / XT-413 diagnostics: backend rejected a Clerk-signed session
    // (missing/inactive account). Beacon before sign-out so BE logs capture
    // volume even when the next navigation drops the SPA.
    reportClientEvent({
      kind: 'orphaned_session',
      message:
        'auth.me failed or user missing with Clerk signed-in — signing out',
    });
    void signOut({ redirectUrl: '/sign-in' });
  }, [signOut]);

  return (
    <div className={css.loading}>
      <Text variant="muted">Redirecting…</Text>
    </div>
  );
};
